import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Image as ImageIcon, Loader2, X } from 'lucide-react';
import KitMarketingFlyer, {
  KIT_MARKETING_DEFAULT_DETAILS_POS,
  KIT_MARKETING_DEFAULT_FABRIC_POS,
  KIT_MARKETING_DEFAULT_FABRIC_SIZE,
  KIT_MARKETING_DEFAULT_IMAGE_TRANSFORM,
} from './KitMarketingFlyer';
import {
  buildKitMarketingCopy,
  resolveFabricImageUrl,
  resolveKitImageUrl,
} from './buildKitMarketingCopy';
import {
  exportKitMarketingImage,
  exportKitMarketingPdf,
} from '../../utils/kitMarketingExportPdf';
import '../fabricTemplates/fabricTemplates.css';

const MAX_KITS = 3;

function buildDefaultTransforms(kitCount) {
  return {
    fabric: { ...KIT_MARKETING_DEFAULT_IMAGE_TRANSFORM },
    fabricSize: KIT_MARKETING_DEFAULT_FABRIC_SIZE,
    fabricPos: { ...KIT_MARKETING_DEFAULT_FABRIC_POS },
    detailsPos: { ...KIT_MARKETING_DEFAULT_DETAILS_POS },
    detailsWidthPct: null,
    kits: Array.from({ length: Math.max(kitCount, 0) }, () => ({
      ...KIT_MARKETING_DEFAULT_IMAGE_TRANSFORM,
    })),
  };
}

/**
 * Fullscreen editor for kit marketing flyer — editable copy + PDF / PNG export.
 */
export default function KitMarketingFlyerEditor({
  open,
  onClose,
  fabricDetails,
  kitData,
  fabricImage,
  selectedKits = [],
  brand,
  showNotification,
}) {
  const flyerRef = useRef(null);
  const kits = useMemo(() => (selectedKits || []).slice(0, MAX_KITS), [selectedKits]);

  const resolvedFabricImage = useMemo(
    () =>
      resolveFabricImageUrl({
        fabricDetails,
        kitData,
        fabricImage,
      }),
    [fabricDetails, kitData, fabricImage]
  );

  const kitImages = useMemo(() => kits.map(resolveKitImageUrl).filter(Boolean), [kits]);

  const [draftCopy, setDraftCopy] = useState(() =>
    buildKitMarketingCopy({
      fabricDetails,
      kitData,
      brand,
      selectedKits: kits,
    })
  );
  const [imageTransforms, setImageTransforms] = useState(() =>
    buildDefaultTransforms(kitImages.length)
  );
  const [exportingKind, setExportingKind] = useState(null); // 'pdf' | 'image' | null

  useEffect(() => {
    if (!open) return;
    setDraftCopy(
      buildKitMarketingCopy({
        fabricDetails,
        kitData,
        brand,
        selectedKits: kits,
      })
    );
    setImageTransforms(buildDefaultTransforms(kitImages.length));
  }, [open, fabricDetails, kitData, brand, kits, kitImages.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const buildExportPayload = () => {
    const title = draftCopy?.productTitle || 'fabric-marketing';
    const safe = String(title).toLowerCase().replace(/\s+/g, '-');
    return {
      copy: draftCopy,
      fabricImageUrl: resolvedFabricImage,
      kitImageUrls: kitImages,
      imageTransforms,
      documentTitle: title,
      fileName: `${safe}-marketing`,
    };
  };

  const handleExportPdf = async () => {
    if (exportingKind) return;
    setExportingKind('pdf');
    try {
      await exportKitMarketingPdf(buildExportPayload());
      showNotification?.('Marketing PDF downloaded', 'success');
    } catch (err) {
      console.error('Kit marketing PDF export failed:', err);
      showNotification?.(err?.message || 'Failed to export PDF', 'error');
    } finally {
      setExportingKind(null);
    }
  };

  const handleExportImage = async () => {
    if (exportingKind) return;
    setExportingKind('image');
    try {
      const result = await exportKitMarketingImage(buildExportPayload());
      showNotification?.(
        `Marketing image downloaded (${result.width}×${result.height} PNG)`,
        'success'
      );
    } catch (err) {
      console.error('Kit marketing image export failed:', err);
      showNotification?.(err?.message || 'Failed to export image', 'error');
    } finally {
      setExportingKind(null);
    }
  };

  if (!open) return null;

  const exporting = Boolean(exportingKind);

  return createPortal(
    <div
      className="ftl-modal-backdrop !z-[200] !p-0 sm:!p-4"
      style={{ zIndex: 200 }}
      role="dialog"
      aria-modal="true"
      aria-label="Marketing material editor"
    >
      <div className="ftl-modal-panel bg-[#1a1620] flex flex-col w-full h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[min(92vh,900px)] rounded-none sm:rounded-2xl">
        {/* Header — stacked on mobile so title isn't crushed by export buttons */}
        <div className="shrink-0 border-b border-white/10 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-white font-semibold text-base sm:text-lg truncate">
                Marketing material
              </h2>
              <p className="hidden sm:block text-white/55 text-xs mt-0.5">
                Portrait poster · zoom to enlarge · drag to pan when zoomed
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition shrink-0"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportImage}
              disabled={exporting}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 text-sm font-semibold hover:bg-white/15 disabled:opacity-60 transition"
              title="Download lossless PNG at print resolution"
            >
              {exportingKind === 'image' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ImageIcon size={16} />
              )}
              <span>{exportingKind === 'image' ? 'Exporting…' : 'Export image'}</span>
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white text-[#1a2744] text-sm font-semibold hover:bg-[#f3efe6] disabled:opacity-60 transition"
            >
              {exportingKind === 'pdf' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              <span>{exportingKind === 'pdf' ? 'Exporting…' : 'Export PDF'}</span>
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-2 sm:p-5 bg-[#121018]">
          <div className="ftl-flyer--editable w-full max-w-[min(100%,28rem)] sm:max-w-lg mx-auto">
            <div ref={flyerRef} className="w-full">
              <KitMarketingFlyer
                copy={draftCopy}
                onCopyChange={setDraftCopy}
                editable
                fabricImage={resolvedFabricImage}
                kitImages={kitImages}
                imageTransforms={imageTransforms}
                onImageTransformsChange={setImageTransforms}
              />
            </div>
            <p className="mt-2 sm:mt-3 text-center text-[0.65rem] sm:text-xs text-white/50 px-2 leading-snug">
              Drag overlays to move · corner grip to resize · then export
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export { MAX_KITS as KIT_MARKETING_MAX_KITS };
